import * as React from 'react';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { CalibrationItem, Campaign } from 'types';

import { isCalibratePromotionNominations } from 'utils/util/features';

type CalibrationContextType = {
  setCalibrationItems: React.Dispatch<React.SetStateAction<CalibrationItem[]>>;
  isCalibrating: boolean;
  calibratePromotionNominationsEnabled?: boolean;
  surveyResponseIdToCalibrationItems: Record<number, CalibrationItem[]>;
  campaign?: Campaign;
  calibrationViewId?: number;
};

const CalibrationContext = createContext<CalibrationContextType | null>(null);

const CalibrationProvider: FC<
  PropsWithChildren<{
    setCalibrationItems: React.Dispatch<
      React.SetStateAction<CalibrationItem[]>
    >;
    isCalibrating: boolean;
    campaign?: Campaign;
    surveyResponseIdToCalibrationItems: Record<number, CalibrationItem[]>;
    calibrationViewId?: number;
  }>
> = ({
  children,
  setCalibrationItems,
  isCalibrating,
  campaign,
  surveyResponseIdToCalibrationItems,
  calibrationViewId,
}) => {
  const value: CalibrationContextType = useMemo(() => {
    const calibratePromotionNominationsEnabled =
      !!campaign && isCalibratePromotionNominations(campaign);
    return {
      setCalibrationItems,
      isCalibrating,
      calibratePromotionNominationsEnabled,
      surveyResponseIdToCalibrationItems,
      campaign,
      calibrationViewId,
    };
  }, [
    isCalibrating,
    setCalibrationItems,
    campaign,
    surveyResponseIdToCalibrationItems,
    calibrationViewId,
  ]);

  return (
    <CalibrationContext.Provider value={value}>
      {children}
    </CalibrationContext.Provider>
  );
};

const useCalibration = (): CalibrationContextType => {
  const context = useContext(CalibrationContext);
  if (!context) {
    throw new Error('useCalibration must be used within a CalibrationProvider');
  }
  return context;
};

export { CalibrationProvider, useCalibration };
